import { Suspense } from 'react';
import {
  createBrowserRouter,
  Link,
  Outlet,
  type RouteObject,
  type RouterProviderProps,
} from 'react-router-dom';

import { AppLayout } from '@/components/layouts/AppLayout';
import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { Brand } from '@/components/shared/Brand';
import { buttonVariants } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { aboutRoutes } from '@/modules/about/routes';
import { alertsRoutes } from '@/modules/alerts/routes';
import {
  authRoutes,
  type GuestGateRouteCopy,
  RequireActiveAccount,
  RequireAppEntry,
  RequireSession,
} from '@/modules/auth/routes';
import { callsProtectedRoutes, callsRoutes } from '@/modules/calls/routes';
import { contentRoutes } from '@/modules/content/routes';
import { donationsProtectedRoutes, donationsRoutes } from '@/modules/donations/routes';
import { helpRequestProtectedRoutes, helpRequestRoutes } from '@/modules/help-requests/routes';
import { institutionsProtectedRoutes, institutionsRoutes } from '@/modules/institutions/routes';
import { mapProtectedRoutes, mapRoutes } from '@/modules/map/routes';
import { notesRoutes } from '@/modules/notes/routes';
import { platformRoutes } from '@/modules/platform/routes';
import { playgroundRoutes } from '@/modules/playground/routes';
import { profileProtectedRoutes, profileRoutes } from '@/modules/profile/routes';
import { reunionProtectedRoutes, reunionRoutes } from '@/modules/reunion/routes';

/**
 * Árvore de rotas raiz. Agrega os `routes.tsx` de cada módulo e não define
 * telas de produto — os dois componentes abaixo são boundaries de roteamento,
 * não páginas.
 */

function RouteFallback() {
  return <ArcusLoading className="h-dvh" />;
}

/**
 * Rota inexistente.
 *
 * É a tela que mais recebe quem chegou por link velho — num PWA instalado, com
 * atalho salvo na tela inicial e endereço compartilhado por mensagem, link
 * velho é rotina, não exceção. Por isso ela não é um beco: traz a marca (a
 * pessoa continua **dentro** do Arcus, e não numa página de erro do
 * navegador), diz o motivo mais provável em vez de só constatar a falha, e
 * oferece **dois** destinos — o mapa, que é a home, e o conteúdo, que é o que
 * serve a quem nem conta tem.
 */
function RouteNotFound() {
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <EmptyState
        icon={<Brand variant="symbol" tone="on-light" className="w-16" />}
        title="Página não encontrada"
        // O bloco **é** a tela inteira aqui, então o título é o `<h1>` dela.
        // Sem isto a página não tem cabeçalho nenhum, e quem navega por
        // headings — o atalho mais usado por leitor de tela para se situar —
        // chega num documento mudo.
        titleAs="h1"
        description="O link pode estar desatualizado ou o endereço mudou de lugar."
        action={
          <div className="flex flex-col gap-2">
            <Link to="/" className={buttonVariants({ fullWidth: true })}>
              Ir para o mapa
            </Link>

            <Link
              to="/content"
              className={buttonVariants({ variant: 'secondary', fullWidth: true })}
            >
              Ver guias e telefones
            </Link>
          </div>
        }
      />
    </main>
  );
}

/**
 * Pendura a copy do portão de convidado no `handle` de cada rota do grupo — é
 * de lá que o `RequireSession guest="gate"` lê o que dizer a quem chegou sem
 * conta (auditoria de UX, §1.3).
 *
 * A copy mora aqui, e não no `routes.tsx` de cada módulo, porque é a árvore
 * raiz que decide **quais** rotas ficam atrás da guarda: a frase "isto pede uma
 * conta" pertence a essa decisão, e mudaria junto com ela.
 *
 * ⚠️ Substitui o `handle` da rota. Nenhuma delas tem outro hoje; a primeira que
 * tiver precisa que isto passe a mesclar.
 */
function withGuestGate(routes: RouteObject[], guestGate: GuestGateRouteCopy): RouteObject[] {
  return routes.map((route) => ({ ...route, handle: { guestGate } }));
}

/**
 * Copy por tela. Curta, no tom do app, e sempre dizendo **o que a conta
 * destrava** — não só que ela é exigida. Quando existe um caminho que não
 * precisa de conta, ele vira o terceiro botão (`extraAction`).
 */
const GUEST_GATE_COPY = {
  requests: {
    pageTitle: 'Meus pedidos SOS',
    title: 'Acompanhar pedidos pede uma conta',
    description:
      'Com uma conta, você acompanha os pedidos que fez e, pela sua instituição, atende os da fila. Para pedir socorro agora, não precisa de conta.',
    // DV-24: pedir socorro é a única ação de produção aberta ao convidado.
    extraAction: { label: 'Pedir socorro', to: '/help-request' },
  },
  calls: {
    pageTitle: 'Chamados',
    title: 'Chamados pedem uma conta',
    description: 'Com uma conta, você recebe e responde aos chamados das instituições.',
  },
  notes: {
    pageTitle: 'Bloco de notas',
    title: 'Suas notas ficam na sua conta',
    description:
      'O bloco de notas guarda o que você escreve na sua conta, e ninguém mais vê. Entre ou crie uma conta para começar.',
  },
  admin: {
    pageTitle: 'Administrador',
    title: 'Área do administrador',
    description: 'Esta área é de quem administra o Arcus. Entre com a sua conta para continuar.',
  },
  reunion: {
    pageTitle: 'Buscar abrigados',
    title: 'Buscar abrigados pede uma conta',
    // DV-08: a busca mostra nomes de pessoas acolhidas, e só abre para logados.
    description:
      'A busca mostra nomes de pessoas acolhidas em abrigos. Para proteger quem está lá, ela só abre para quem entrou numa conta.',
    extraAction: { label: 'Ver telefones de emergência', to: '/content/emergency-contacts' },
  },
} as const satisfies Record<string, GuestGateRouteCopy>;

/**
 * O `Suspense` fica aqui, e não dentro de cada rota: toda página é `lazy`, e
 * sem um boundary acima do `Outlet` o primeiro acesso a qualquer rota subiria
 * a promise até a raiz e derrubaria a árvore.
 */
function RootBoundary() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
}

export const appRoutes: RouteObject[] = [
  {
    element: <RootBoundary />,
    children: [
      // As telas de produto ficam sob o AppLayout, que monta a barra inferior
      // uma vez só. O playground fica de fora: é ferramenta, não produto, e não
      // deve herdar a navegação do app.
      //
      // O `RequireAppEntry` envolve só este ramo: as telas de autenticação
      // precisam ser alcançáveis justamente por quem ainda não passou pela
      // guarda, e o playground é ferramenta interna.
      {
        element: <RequireAppEntry />,
        children: [
          {
            element: <AppLayout />,
            children: [
              ...mapRoutes,
              ...alertsRoutes,
              ...contentRoutes,
              ...aboutRoutes,
              ...donationsRoutes,
              ...institutionsRoutes,
              ...profileRoutes,
              // Ações de produção exigem conta (plano de auth v2, §3). O
              // bloco de notas entra por outro motivo, não menos duro: a nota é
              // gravada sob o `profile_id` da pessoa e não existe sem sessão.
              //
              // `guest="gate"`: são telas-destino, com barra. O convidado fica
              // na tela e vê o `GuestGate` com a copy do `handle` (acima), em
              // vez de cair seco no login — quem tocou "Pedidos" precisa saber
              // o que a tela faria antes de decidir criar uma conta.
              {
                element: <RequireSession guest="gate" />,
                children: [
                  // `RequireActiveAccount` (plano do painel do admin, D-05):
                  // conta bloqueada pela plataforma vê a tela de explicação em
                  // vez de qualquer uma destas rotas, mesmo já tendo sessão.
                  {
                    element: <RequireActiveAccount />,
                    children: [
                      ...withGuestGate(helpRequestRoutes, GUEST_GATE_COPY.requests),
                      ...withGuestGate(callsRoutes, GUEST_GATE_COPY.calls),
                      ...withGuestGate(notesRoutes, GUEST_GATE_COPY.notes),
                      // A fila da plataforma é listagem com barra inferior, como as
                      // outras telas-destino — não é fluxo de tarefa. Quem não é da
                      // plataforma é devolvido pela própria página; aqui só se exige
                      // sessão, porque a pergunta "é da plataforma?" precisa de uma.
                      ...withGuestGate(platformRoutes, GUEST_GATE_COPY.admin),
                      // Reencontro exige conta pela DV-08 — a RLS devolve zero linha
                      // para convidado, e uma busca que nunca acha ninguém seria
                      // pior que a porta fechada com explicação.
                      ...withGuestGate(reunionRoutes, GUEST_GATE_COPY.reunion),
                    ],
                  },
                ],
              },
            ],
          },
          // Fluxos de tarefa montam **fora** do AppLayout, sem a barra: no meio
          // de um formulário, um toque acidental numa aba descarta o trabalho
          // (decisão de navegação de 2026-07-22, config/navigation.ts).
          // Aqui o convidado continua indo direto ao login com `?next=`: quem
          // abriu um formulário por link quer o formulário logo depois de entrar.
          {
            element: <RequireSession />,
            children: [
              // `RequireActiveAccount` também envolve os fluxos de tarefa: uma
              // conta bloqueada não abre "criar instituição" nem "operação"
              // por chegar direto na URL do formulário.
              {
                element: <RequireActiveAccount />,
                children: [
                  ...institutionsProtectedRoutes,
                  ...callsProtectedRoutes,
                  ...mapProtectedRoutes,
                  ...donationsProtectedRoutes,
                  ...profileProtectedRoutes,
                  // Cadastro de abrigados: formulário longo do abrigo, e por isso
                  // fora do AppLayout como os outros fluxos de tarefa.
                  ...reunionProtectedRoutes,
                ],
              },
            ],
          },
          // ⚠️ **Pedir socorro não exige conta** (DV-24), e é a única ação de
          // produção assim. Fica fora do `RequireSession` de propósito: quem
          // chega sem sessão informa um e-mail no passo 2, e o envio abre uma
          // conta anônima que passa a ser dona do pedido. Em crise, fricção de
          // cadastro custa caro — é a mesma razão de o mapa e os guias serem
          // abertos, levada até a ação que mais importa.
          //
          // `RequireActiveAccount` continua envolvendo a rota: sem sessão ela é
          // transparente (convidado nunca está bloqueado), e com sessão ela
          // impede que uma conta bloqueada abra o formulário. O fluxo continua
          // fora do `AppLayout`, como todo fluxo de tarefa.
          {
            element: <RequireActiveAccount />,
            children: [...helpRequestProtectedRoutes],
          },
        ],
      },
      ...authRoutes,
      ...playgroundRoutes,
      { path: '*', element: <RouteNotFound /> },
    ],
  },
];

// O tipo é anotado explicitamente: o inferido vive em @remix-run/router, que é
// dependência transitiva e não pode ser nomeada daqui (TS2742). `RouterProviderProps`
// é o tipo público equivalente exposto pelo react-router-dom.
export const router: RouterProviderProps['router'] = createBrowserRouter(appRoutes);
